import {
  ArticleTag,
  Card,
  CardContent,
  CardImage,
} from '@cash-rewards/cr-ui-components';
import { ARRAY_ELEMENT_ID, EVENT_NAME } from '@cash-rewards/mparticlejs';
import { clsxm } from '@cash-rewards/reactjs';
import {
  articleClickEventMapper,
  articleProductMapper,
} from '@cash-rewards/reactjs/client';

import NextImage from '@/components/shared/image';

import ShopCarousel from '@/modules/core/components/shopCarousel/ShopCarousel';
import { Article } from '@/modules/shop/models/article';
import excludeb2bArticle from '@/utils/b2bArticles';
import slotArticles from '@/utils/slotArticles';

interface ArticleProps {
  articles: Article[];
}

const FeaturedArticles = ({ articles, ...rest }: ArticleProps) => {
  if (!articles) {
    return null;
  }

  //https://shopgoau.atlassian.net/browse/RETM-499
  const orderedAndScheduledArticles: Article[] = slotArticles(articles);

  //https://shopgoau.atlassian.net/browse/RETM-558
  const excludedB2bArticles = excludeb2bArticle(orderedAndScheduledArticles);

  return (
    <ShopCarousel
      title='Get inspired'
      navigationButtonTopCss='top-14'
      data-test-id='featured-articles-carousel'
      items={articles}
      productMappers={[articleProductMapper]}
      clickEventMappers={[articleClickEventMapper]}
      carouselType={ARRAY_ELEMENT_ID.featured_articles}
      clickEventName={EVENT_NAME.click_web_article}
      impressionEventName={EVENT_NAME.impression_web_carousel}
      {...rest}
    >
      {excludedB2bArticles?.map((article: Article) => {
        const { id, attributes } = article || {};
        const { seo, published_date, title } = attributes;
        return (
          <Card
            key={id}
            className={clsxm('mr-cr-4 w-[256px] lg:mr-cr-8')}
            data-array-position={id}
            data-array-element={`article-card-${id}`}
            data-test-id={`article-card-box-${article.id}`}
            data-element-id={title}
            as='a'
            href={seo?.og?.og_url}
          >
            <CardImage
              aspectRatio='16:9'
              imageHoverAnimation={true}
              as={NextImage}
              src={seo?.og?.og_image?.data?.attributes?.url}
              alt={seo?.og?.og_image?.data?.attributes?.alternativeText}
              loading='eager'
              priority={0}
              width={256}
              height={144}
            />
            <CardContent>
              <div
                className='flex flex-col gap-1'
                data-test-id='article-card-content'
              >
                {published_date && (
                  <ArticleTag
                    variant='NoStack'
                    date={new Date(published_date)}
                  />
                )}
                {seo?.og?.og_title && (
                  <div className='flex flex-col'>
                    <h3
                      className='line-clamp-2 font-primary text-cr-body-lg-800 text-neutral-500'
                      data-test-id='article-card-title'
                    >
                      {seo?.og?.og_title}
                    </h3>
                  </div>
                )}
                {seo?.og?.og_description && (
                  <p
                    data-test-id='article-card-description'
                    className='ellipsify text-cr-body-lg-400'
                  >
                    {seo?.og?.og_description}
                  </p>
                )}
                {seo?.og?.og_url && (
                  <span
                    data-test-id='article-card-dummy-url'
                    className='text-cr-body-lg-700 text-primary-500'
                  >
                    Read more
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </ShopCarousel>
  );
};

export default FeaturedArticles;
